import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Lock } from "lucide-react";
import { Product } from "../types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useLocation } from "@/contexts/LocationContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import {
  ClientTargetPicker,
  ClientTargetChip,
} from "./ClientTargetPicker";
import { useConnectedClients } from "../hooks/useConnectedClients";
import { joinParts } from "../utils/joinParts";
import { filterToTargetOrg } from "../utils/filterToTargetOrg";

const SHARED_LOCATIONS_ENABLED =
  (import.meta.env.VITE_SHARED_LOCATIONS_ENABLED ?? "") === "true";

type Direction = "to_unavailable" | "to_available";

interface UnavailableAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  products: Product[];
  direction: Direction;
  /**
   * BLU-3xx Phase 47.01.2-B-04: optional `targetOrganizationId` routes
   * unavailable adjustments to the correct row owner at shared 3PL
   * locations.
   *
   * BLU-3xx Phase 47.01.2-B-fix3.1: takes the resolved Product (not a
   * sku string). The modal already has the resolved product (cross-org
   * filter via ARCH-1 fix; own-org via the products prop).
   */
  onSubmit: (params: {
    product: Product;
    quantity: number;
    reason?: string;
    targetOrganizationId?: string;
  }) => Promise<boolean>;
}

const UnavailableAdjustmentModal: React.FC<UnavailableAdjustmentModalProps> = ({
  isOpen,
  onClose,
  products,
  direction,
  onSubmit,
}) => {
  const { selectedLocation } = useLocation();
  const { organization } = useOrganization();
  const { t } = useTranslation('inventory');
  const [selectedSku, setSelectedSku] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(1);
  const [reason, setReason] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  // BLU-3xx Phase 47.01.2-B-04: cross-org target. Required at shared
  // locations, hidden elsewhere.
  const [targetOrgId, setTargetOrgId] = useState<string | null>(null);

  // Reset target org each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setTargetOrgId(null);
  }, [isOpen, selectedLocation?.id]);

  // BLU-3xx Phase 47.01.2-B-fix3: clear stale SKU when target org changes.
  useEffect(() => {
    setSelectedSku("");
    setSearchQuery("");
  }, [targetOrgId]);

  // BLU-3xx Phase 47.01.2-B-fix3 (post-review ARCH-1): UnavailableAdjustment
  // operates on EXISTING inventory (move stock between available/unavailable
  // buckets — damaged units, redo holds, etc). Catalog-only data is wrong
  // here: maxQuantity derives from availableCount/unavailableCount which a
  // catalog row's hardcoded 0 would render unsubmittable. Filter the cross-
  // org-aware `products` prop by targetOrgId instead. Phase A-01's
  // `getProducts` widening already includes connected-client rows when the
  // caller is at a shared location with an active junction.
  // BLU-669: this modal's hand-rolled filter was extracted to the shared
  // `filterToTargetOrg` helper (now also used by useTargetCatalog for the
  // Receive/Stocktake modals and the bulk upload preview) — identical
  // semantics, one predicate to maintain.
  const selectableProducts: Product[] = useMemo(
    () => filterToTargetOrg(products, targetOrgId, organization?.id),
    [products, targetOrgId, organization?.id],
  );

  // BLU-3xx Phase 47.01.2-B-fix3: focus-jump on picker→SKU activation.
  const skuTriggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    if (!targetOrgId) return;
    const id = window.setTimeout(() => {
      skuTriggerRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(id);
  }, [isOpen, targetOrgId]);

  const filteredProducts = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return selectableProducts;
    }
    return selectableProducts.filter((product) => {
      const haystack = [
        product.sku,
        product.category.type,
        product.category.color,
        product.category.size,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [selectableProducts, searchQuery]);

  const selectedProduct = useMemo(
    () => selectableProducts.find((p) => p.sku === selectedSku),
    [selectableProducts, selectedSku]
  );

  // BLU-669: target-agnostic — true when ANY target (own org included) is
  // picked and no rows of that target's stock exist at this location. The
  // pre-669 cross-org-only shape left the own-org branch a silent blank list.
  const isTargetEmpty = !!targetOrgId && selectableProducts.length === 0;
  const isOwnOrgTargetEmpty = isTargetEmpty && targetOrgId === organization?.id;

  const unavailableCount = selectedProduct?.unavailable ?? 0;
  const availableCount =
    (selectedProduct?.stockLevel ?? 0) -
    (selectedProduct?.committed ?? 0) -
    (selectedProduct?.unavailable ?? 0);

  // BLU-3xx Phase 47.01.2-B-fix2 NEW-FH1.5: i18n the direction-conditional
  // title + description so zh-CN renders idiomatically. Reuses existing
  // `unavailableModal.*` keys.
  const dialogTitle =
    direction === "to_unavailable"
      ? t('unavailableModal.moveToUnavailable')
      : t('unavailableModal.releaseFromUnavailable');

  const description =
    direction === "to_unavailable"
      ? t('unavailableModal.moveDesc')
      : t('unavailableModal.releaseDesc');

  // BLU-3xx Phase 47.01.2-B-04 + B-fix MED-6: shared cache fetch via
  // `useConnectedClients` for picker visibility + chip name resolution.
  const { clients: connectedClients } = useConnectedClients({
    locationId: selectedLocation?.id,
    enabled:
      isOpen && SHARED_LOCATIONS_ENABLED && !!organization?.id,
  });

  const showPicker =
    SHARED_LOCATIONS_ENABLED &&
    !!organization?.id &&
    !!selectedLocation?.id &&
    connectedClients.length > 0;

  const isOwnOrgTarget = !!targetOrgId && targetOrgId === organization?.id;

  // BLU-3xx Phase 47.01.2-B-fix2 NEW-FH1.4: i18n the (You) suffix
  // template + the "Your org" fallback so zh-CN users see localised
  // strings.
  const resolvedTargetName = useMemo(() => {
    if (!targetOrgId) return null;
    if (targetOrgId === organization?.id) {
      return organization?.name
        ? t('crossOrg.picker.youSuffix', { name: organization.name })
        : t('crossOrg.picker.yourOrgFallback');
    }
    const match = connectedClients.find((c) => c.id === targetOrgId);
    return match?.name ?? null;
  }, [targetOrgId, organization?.id, organization?.name, connectedClients, t]);

  const handleSubmit = async () => {
    if (!selectedSku || quantity <= 0 || submitting) return;
    if (showPicker && !targetOrgId) return; // gate enforced

    // BLU-3xx Phase 47.01.2-B-fix3.1: pass resolved Product (already in
    // `selectableProducts` post-ARCH-1 cross-org filter), not a string.
    if (!selectedProduct) return;

    setSubmitting(true);
    try {
      const succeeded = await onSubmit({
        product: selectedProduct,
        quantity,
        reason: reason.trim() || undefined,
        targetOrganizationId: targetOrgId ?? undefined,
      });
      if (succeeded) {
        setSelectedSku("");
        setQuantity(1);
        setReason("");
        setSearchQuery("");
        setTargetOrgId(null);
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const maxQuantity =
    direction === "to_unavailable" ? Math.max(availableCount, 0) : Math.max(unavailableCount, 0);
  const disableSubmit =
    !selectedSku ||
    quantity <= 0 ||
    maxQuantity <= 0 ||
    quantity > maxQuantity ||
    (showPicker && !targetOrgId);

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
  };

  // BLU-3xx Phase 47.01.2-B-04: Cmd/Ctrl+Enter respects picker gate.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      if (!disableSubmit && !submitting) {
        event.preventDefault();
        void handleSubmit();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[450px]" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {description}
          </DialogDescription>
          {targetOrgId && resolvedTargetName && (
            <div className="pt-1">
              <ClientTargetChip
                resolvedName={resolvedTargetName}
                label={t('crossOrg.picker.labelAdjusting')}
                isOwnOrg={isOwnOrgTarget}
              />
            </div>
          )}
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {/* BLU-3xx Phase 47.01.2-B-04 + B-fix3: cross-org target picker.
              Reordered above SKU — picker is the first decision; SKU
              dropdown gates on it. Subtle bordered group signals "this
              is the gating decision". */}
          {showPicker && organization && (
            <div className="rounded-md border border-border/60 bg-muted/20 p-3">
              <ClientTargetPicker
                value={targetOrgId}
                onChange={setTargetOrgId}
                ownOrgId={organization.id}
                ownOrgName={organization.name ?? t('crossOrg.picker.yourOrgFallback')}
                locationId={selectedLocation?.id ?? ''}
                required
                label={t('crossOrg.picker.labelAdjusting')}
              />
            </div>
          )}

          <div className="grid gap-2">
            <Label>{t('unavailableModal.productSkuLabel')}</Label>
            <Select value={selectedSku} onValueChange={setSelectedSku}>
              <SelectTrigger
                ref={skuTriggerRef}
                disabled={showPicker && !targetOrgId}
                className="transition-colors duration-200"
              >
                {showPicker && !targetOrgId ? (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Lock className="h-4 w-4" />
                    {t('crossOrg.modal.skuPickClientFirst')}
                  </span>
                ) : (
                  <SelectValue placeholder={t('unavailableModal.selectProduct')} />
                )}
              </SelectTrigger>
              <SelectContent>
                <div className="p-2">
                  <Input
                    placeholder={t('unavailableModal.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    autoFocus
                    className="mb-2"
                  />
                </div>
                {/* No "loading catalog" state here — Unavailable operates
                    on existing inventory only (post-ARCH-1 fix). The
                    products prop is already cross-org-aware via A-01. */}
                {isOwnOrgTargetEmpty ? (
                  // BLU-669: own-org target with no own stock rows here —
                  // previously a silent blank list.
                  <div className="p-3 text-center">
                    <p className="text-sm text-muted-foreground">
                      {t('crossOrg.modal.skuEmptyOwnCatalog', {
                        name: organization?.name ?? t('crossOrg.picker.yourOrgFallback'),
                      })}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('crossOrg.modal.skuEmptyOwnStockHelp')}
                    </p>
                  </div>
                ) : isTargetEmpty && resolvedTargetName ? (
                  <div className="p-3 text-center">
                    <p className="text-sm text-muted-foreground">
                      {t('crossOrg.modal.unavailableNoStockYet', {
                        client: resolvedTargetName,
                      })}
                    </p>
                  </div>
                ) : (
                  filteredProducts.map((product) => (
                    <SelectItem key={product.sku} value={product.sku} className="group">
                      <div className="flex flex-col">
                        <span>{product.sku}</span>
                        <span className="text-xs text-muted-foreground group-data-[state=checked]:text-accent-foreground group-data-[highlighted]:text-accent-foreground">
                          {joinParts([product.category.type, product.category.size], ' - ')}
                          {product.unavailable
                            ? ` • ${t('unavailableModal.unavailableLabel')}: ${product.unavailable}`
                            : ""}
                        </span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {selectedProduct && (
            <div className="grid gap-2 rounded-lg bg-muted/30 p-3 text-sm">
              <div>
                <strong>{t('unavailableModal.availableLabel')}:</strong> {Math.max(availableCount, 0)}
              </div>
              <div>
                <strong>{t('unavailableModal.unavailableLabel')}:</strong> {unavailableCount}
              </div>
              <div>
                <strong>{t('unavailableModal.committedLabel')}:</strong> {selectedProduct.committed ?? 0}
              </div>
            </div>
          )}

          <div className="grid gap-2">
            <Label>{t('unavailableModal.quantityLabel')}</Label>
            <Input
              type="number"
              min={1}
              max={maxQuantity || undefined}
              value={Number.isNaN(quantity) ? "" : quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 0)}
              placeholder={t('unavailableModal.enterQuantity')}
            />
            {selectedProduct && (
              <p className="text-xs text-muted-foreground">
                {direction === "to_unavailable"
                  ? t('unavailableModal.maxAvailable', { count: Math.max(availableCount, 0) })
                  : t('unavailableModal.currentlyUnavailable', { count: unavailableCount })}
              </p>
            )}
          </div>

          <div className="grid gap-2">
            <Label>{t('unavailableModal.reasonLabel')}</Label>
            <Textarea
              placeholder={t('unavailableModal.reasonPlaceholder')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('unavailableModal.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={disableSubmit || submitting}
            className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90 disabled:opacity-50"
            aria-keyshortcuts="Meta+Enter"
            aria-busy={submitting || undefined}
          >
            {submitting && (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
            )}
            {direction === "to_unavailable"
              ? t('unavailableModal.moveToUnavailable')
              : t('unavailableModal.releaseToAvailable')}
            {/* BLU-3xx Phase 47.01.2-B-fix MED-8: Cmd/Ctrl+Enter hint. */}
            {!submitting && (
              <kbd className="ml-1 rounded border border-border/60 bg-white/10 px-1 text-[10px] font-mono">
                ⌘↵
              </kbd>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UnavailableAdjustmentModal;
